import Page from "components/common/page/Page";
import { useEffect, useState } from "react";
import { gql } from "@apollo/client";
import { ComponentStateTypes } from "components/common/ComponentState";
import { graph } from "config/axios-context";
import { SaveSaleMutation, SaveSaleMutationVariables, SaleFormDataQuery, SaleFormDataQueryVariables, SaleTypes, SaleInput } from "types/graphql";
import { useParams, useSearchParams } from "react-router-dom";
import { saveSaleMutation } from "types/agriculture/sale-types";
import { useContext } from "react";	
import { getAppContext } from 'config/AppContext';
import useAppNavigation from "utilities/hooks/useAppNavigate";
import { AppRoutes } from "config/router";
import { DateFormats, DateUtil } from "utilities/date-utilities";
import SaleFormInputs, { SaleType } from "components/entities/agriculture/sale/form/SaleFormInputs";
import { PersonType } from "types/person-types";
import { IdName } from "types/common-types";

const saleFormDataQuery = gql`
query saleFormData($id: Int, $templateId: Int){
    season(where: {active: {eq: true}}){
        id,
        name,
        active
    },
    sale(id: $id) {
        name,
        season {
            name,
            id,
            active
        },
        date,
        person {
            name,
            id
        },
        wallet {
            name,
            id,
            balance
        }
        fruit {
            name,
            id
        },
        kg,
        pricePerKg,
        total,
        category {
            name,
            id
        },
        cartQuantity,
        chamber,
        type,
        note
    }
    saleTemplate(id: $templateId) {
        name,
        season {
            name,
            id,
            active
        },
        date,
        person {
            name,
            id
        },
        fruit {
            name,
            id
        },
        kg,
        pricePerKg,
        total,
        category {
            name,
            id
        },
        cartQuantity,
        chamber,
        type,
        note
    }
}
`;

type SaleFormPageUrlParamsType = {
    id?: string
}

const SaleFormPage = () => {

    const navigate = useAppNavigation();
    const context = getAppContext(useContext);
    const params = useParams<SaleFormPageUrlParamsType>();
    const [searchParams] = useSearchParams();
    const [model, setModel] = useState<SaleType>({
        id: 0,
        name: '',
        date: new Date(),
        fruit: {
            id: 1,
            name: 'Mandarina'
        },
        kg: '',
        pricePerKg: '',
        total: '',
        chamber: false,
        cartQuantity: '',
        type: SaleTypes.Wholesale,
        note: ''
    });

    const [componentState, setComponentState] = useState<ComponentStateTypes>();

    useEffect(() => {
        loadData().then();
    }, []);

    const loadData = async () => {
        setComponentState('loading');
        const response = await graph<SaleFormDataQuery, SaleFormDataQueryVariables>({
            query: saleFormDataQuery,
            variables: {
                id: Number(params.id || 0),
                templateId: Number(searchParams.get("template")) || 0
            }
        });
        
        if(response.errors) {
            setComponentState('error');
            console.error(response);
            return;
        }
        
        let theModel: SaleType = {
            ...model,
            season: response.data!.season!,
            name: `Sale ${DateUtil.format(new Date(), DateFormats.SHORT)} ${response.data!.season?.name}`,
            date: new Date()
        };

        if(response.data?.sale || response.data?.saleTemplate) {
            const sale = response.data?.sale || response.data?.saleTemplate;
            theModel = {
                id: Number(params.id || 0),
                name: response.data.sale?.name || theModel.name,
                season: sale?.season || theModel.season,
                date: sale?.date ? new Date(sale?.date) : new Date(),
                person: sale?.person as PersonType,
                fruit: sale?.fruit as IdName,
                kg: sale?.kg,
                pricePerKg: sale?.pricePerKg,
                total: sale?.total,
                chamber: sale?.chamber || false,
                cartQuantity: sale?.cartQuantity,
                fruitCategory: sale?.category as IdName,
                type: sale?.type || undefined,
                note: sale?.note || '',
                wallet: sale && 'wallet' in sale ? sale.wallet : undefined
            }
        }

        setModel(theModel);
        setComponentState('');
    }

    const onSubmit = async (model: SaleType) => {
        const saveModel: SaleInput = {
            cartQuantity: Number(model.cartQuantity) || 0,
            chamber: model.chamber ? true : false,
            date: DateUtil.toIsoDate(model.date!),
            fruitId: model.fruit!.id,
            id: Number(params.id) || 0,
            kg: Number(model.kg),
            name: model.name,
            personId: model.person!.id,
            pricePerKg: Number(model.pricePerKg),
            seasonId: model.season!.id,
            total: Number(model.total),
            type: model.type!,
            categoryId: model.fruitCategory!.id,
            note: model.note,
            walletId: model.wallet ? model.wallet.id : undefined
        }

        try {
            const response = await graph<SaveSaleMutation, SaveSaleMutationVariables>({
                query: saveSaleMutation,
                variables: {
                    sale: saveModel
                }
            });

            if(response.data?.sale) {
                navigate(AppRoutes.AGRICULTURE_SALE_VIEW, {id: response.data!.sale!.id})
            } else {
                throw response.errors;
            }
        } catch (error: any) {
            context.notify('error', 'form.submit.error', error.message);
            throw error;
        }
    }

    return (
        <Page title="pages.agriculture.sale.form" state={componentState}>
                <SaleFormInputs 
                    name="agriculture-sale-form-page"
                    model={model}
                    onSubmit={onSubmit}
                />
        </Page>
    );
};

export default SaleFormPage;
